import {useEffect, useMemo, useState, useRef} from "react";
import {Form, useNavigate, useSubmit, useSearchParams} from "react-router-dom";
import {
    Autocomplete,
    Button,
    Box,
    Grid,
    MenuItem,
    Select,
    TextField,
    Modal,
    Container,
    Paper,
    Typography,
    FormControl,
    Dialog,
    Stack,
    IconButton,
    InputLabel,
    useMediaQuery, Checkbox, FormControlLabel
} from "@mui/material";
import {format, addDays, startOfWeek, addWeeks, parse, startOfDay, parseISO, isBefore, isEqual} from "date-fns";
import DoneIcon from '@mui/icons-material/Done';
import {uk} from 'date-fns/locale/uk';
import {DateRangePicker} from 'react-date-range';
import 'react-date-range/dist/styles.css'; // main css file
import 'react-date-range/dist/theme/default.css'; // theme css file
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import {showSnackbar} from "../../components/snackbarUtils";

export default function FilterComponent({loaderData}) {
    const {filter, searchParams} = loaderData;
    const [urlSearchParams] = useSearchParams();

    const bookingFromParam = urlSearchParams.get('booking_from') ?? searchParams?.booking_from?.[0] ?? null;
    const bookingToParam = urlSearchParams.get('booking_to') ?? searchParams?.booking_to?.[0] ?? null;
    const autoCompleteOptions = useMemo(() => {
        const cityOptions = (filter?.location ?? []).map(el => ({
            label: el.name,
            id: el.id,
            identifier: el.identifier ?? el.slug ?? el.id,
            type: el.ctype || 'city',  // Используем el.ctype как главный источник типа
        }));

        const hotelOptions = (filter?.hotels ?? []).map(hotel => ({
            label: hotel.name,
            id: hotel.id,
            identifier: hotel.identifier ?? hotel.slug ?? hotel.id,
            type: hotel.ctype || 'hotel',
        }));

        return [...cityOptions, ...hotelOptions];
    }, [filter]);

    useEffect(() => {
        const locationId = searchParams?.location_id?.[0];
        const locationType = searchParams?.location_type?.[0];

        if (locationId && locationType && autoCompleteOptions.length > 0) {
            const matchedOption = autoCompleteOptions.find(
                opt => opt.identifier?.toString() === locationId && opt.type === locationType
            );
            if (matchedOption) {
                setSelectedLocation(matchedOption);
            }
        }
    }, [searchParams, autoCompleteOptions]);

    const mealLabels = {
        breakfast: "Сніданок",
        lunch: "Обід",
        dinner: "Вечеря"
    };

    const [meals, setMeals] = useState(searchParams?.meals || []);
    const [petsAllowed, setPetsAllowed] = useState(searchParams?.pets_allowed?.[0] === 'true');

    const navigate = useNavigate();

    useEffect(() => {
        if (bookingFromParam || bookingToParam) {
            const now = new Date();

            const bookingFromDate = bookingFromParam ? parseISO(bookingFromParam) : new Date();
            const bookingToDate = bookingToParam ? parseISO(bookingToParam) : bookingFromDate;

            bookingFromDate.setHours(0, 0, 0, 0);
            bookingToDate.setHours(0, 0, 0, 0);
            now.setHours(0, 0, 0, 0);

            if (isBefore(bookingFromDate, now) && !isEqual(bookingFromDate, now)) {
                showSnackbar("Помилка запиту: Дата бронювання у минулому!", "error", {
                    sx: {backgroundColor: "#ff0000", color: "white"},
                });
                navigate("/");
            } else if (isBefore(bookingFromDate, now)) {
                showSnackbar("Помилка запиту: Дата бронювання у минулому!", "error", {
                    sx: {backgroundColor: "#ff0000", color: "white"},
                });
                navigate("/");
            } else if (isBefore(bookingToDate, bookingFromDate)) {
                showSnackbar("Помилка запиту: Дата виїзду не може бути раніше дати заїзду!", "error", {
                    sx: {backgroundColor: "#ff0000", color: "white"},
                });
                navigate("/");
            }
        }
    }, [bookingFromParam, bookingToParam, navigate]);

    const [selectedLocation, setSelectedLocation] = useState(null);

    const useToday = () => {
        const [today, setToday] = useState(startOfDay(new Date()));

        useEffect(() => {
            const now = new Date();
            const msUntilMidnight = startOfDay(addDays(now, 1)) - now;

            const timeout = setTimeout(() => {
                setToday(startOfDay(new Date()));
            }, msUntilMidnight);

            return () => clearTimeout(timeout);
        }, []);

        return today;
    };

    const today = useToday();
    const startDateIfNone = useMemo(() => today, [today]);

    const initialStartDate = bookingFromParam
        ? parse(bookingFromParam, 'yyyy-MM-dd', new Date())
        : startDateIfNone;
    const initialEndDate = bookingToParam
        ? parse(bookingToParam, 'yyyy-MM-dd', new Date())
        : addDays(startDateIfNone, 1);

    const [selectionRange, setSelectionRange] = useState({
        startDate: initialStartDate,
        endDate: initialEndDate,
        key: 'selection',
    });

// 👇 НОВОЕ – синхронизация с урлом
    useEffect(() => {
        const start = bookingFromParam
            ? parse(bookingFromParam, 'yyyy-MM-dd', new Date())
            : startDateIfNone;

        const end = bookingToParam
            ? parse(bookingToParam, 'yyyy-MM-dd', new Date())
            : addDays(startDateIfNone, 1);

        setSelectionRange(prev => ({
            ...prev,
            startDate: start,
            endDate: end,
        }));
    }, [bookingFromParam, bookingToParam, startDateIfNone]);

    const handleSelectDateRange = (ranges) => {
        setSelectionRange(ranges.selection);
    };


    const [showRangeModal, setShowRangeModal] = useState(false);
    const handleModalRangePicker = () => {
        setShowRangeModal(prev => !prev);
    }
    const submit = useSubmit();
    const handleSubmit = (event) => {
        event.preventDefault();

        const formData = new FormData(event.target);

        // Очистити локальний кеш ПЕРЕД відправкою
        Object.keys(localStorage).forEach(key => {
            if (key.startsWith('filter_cache_') || key.startsWith('booking_search_')) {
                localStorage.removeItem(key);
            }
        });

        // Перевірка міста: беремо location_id
        const locationId = formData.get('location_id');
        if (!locationId) {
            showSnackbar("Будь ласка, оберіть місто.", "error", {
                sx: {backgroundColor: "#ff0000", color: "white"},
            });
            return;
        }

        // Перевірка віку дітей
        const numChildren = parseInt(formData.get('children') ?? 0, 10);
        const childrenAges = formData.getAll('children_ages');
        if (childrenAges.length !== numChildren || childrenAges.some(age => age === "")) {
            showSnackbar("Будь ласка, вкажіть вік всіх дітей.", "error", {
                sx: {backgroundColor: "#ff0000", color: "white"},
            });
            return;
        }

        submit(formData, {method: 'get', action: '/search'});
    };

    const daysShort = {
        пон: 'пн',
        вів: 'вт',
        сер: 'ср',
        чтв: 'чт',
        птн: 'пт',
        суб: 'сб',
        нед: 'нд',
    };

    const isMobile = window.innerWidth <= 768; // Определение мобильного устройства

    const customLocale = {
        ...uk,
        localize: {
            ...uk.localize,
            day: (narrowDay, {width}) => {
                const daysShort = {
                    Mon: 'пн',
                    Tue: 'вт',
                    Wed: 'ср',
                    Thu: 'чт',
                    Fri: 'пт',
                    Sat: 'сб',
                    Sun: 'нд',
                };
                return width === 'abbreviated' ? daysShort[format(addDays(new Date(), narrowDay), 'eee')] : uk.localize.day(narrowDay, {width});
            },
        },
    };

    useEffect(() => {
        if (isMobile && showRangeModal && window.innerWidth < 375) {
            setTimeout(() => {
                const dateDisplayWrapper = document.querySelector('.rdrDateDisplayWrapper');
                if (dateDisplayWrapper) {
                    dateDisplayWrapper.style.width = '85%';
                }
                const rdrMonthAndYearWrapper = document.querySelector('.rdrMonthAndYearWrapper');
                if (rdrMonthAndYearWrapper) {
                    rdrMonthAndYearWrapper.style.width = '90%';
                }
                const rdrMonths = document.querySelectorAll('.rdrMonth'); // Находим все элементы
                rdrMonths.forEach((month) => {
                    month.style.width = '80%'; // Применяем стиль ко всем
                });
            }, 300); // Даем 300 мс на рендеринг
        } else if (isMobile && showRangeModal && window.innerWidth >= 375 && window.innerWidth < 425) {
            setTimeout(() => {
                const dateDisplayWrapper = document.querySelector('.rdrDateDisplayWrapper');
                if (dateDisplayWrapper) {
                    dateDisplayWrapper.style.width = '100%';
                }
                const rdrMonthAndYearWrapper = document.querySelector('.rdrMonthAndYearWrapper');
                if (rdrMonthAndYearWrapper) {
                    rdrMonthAndYearWrapper.style.width = '100%';
                }
                const rdrMonths = document.querySelectorAll('.rdrMonth'); // Находим все элементы
                rdrMonths.forEach((month) => {
                    month.style.width = '100%'; // Применяем стиль ко всем
                });
            }, 300); // Даем 300 мс на рендеринг
        }
    }, [isMobile, showRangeModal]); // Следим за `isMobile` и `showRangeModal`

    // const handleSearchSubmit = (event) => {
    //     event.preventDefault(); // Остановить стандартную отправку формы
    //
    //     // Очистить кэш
    //     Object.keys(localStorage).forEach(key => {
    //         if (key.startsWith('filter_cache_') || key.startsWith('booking_search_')) {
    //             localStorage.removeItem(key);
    //         }
    //     });
    //
    //     // Отправить форму через useSubmit()
    //     const formData = new FormData(event.target.form);
    //     submit(formData, {method: 'get', action: '/search'});
    // };

    return (
        <>
            <Form action='/search' onSubmit={handleSubmit}>
                <Grid container spacing={1}>
                    <Grid item xs={12} md={4}>
                        <Autocomplete
                            disablePortal
                            id="locations"
                            options={autoCompleteOptions}
                            required
                            value={selectedLocation}
                            getOptionLabel={(option) => option?.label || ""}
                            isOptionEqualToValue={(option, value) =>
                                option.id === value?.id && option.type === value?.type
                            }
                            onChange={(event, newValue) => {
                                setSelectedLocation(newValue || null);
                                document.querySelector("input[name='location_id']").value = newValue?.identifier || "";
                                document.querySelector("input[name='location_type']").value = newValue?.type || "";
                            }}
                            renderOption={(props, option, state) => {
                                const inputValue = state.inputValue?.toLowerCase() || '';
                                const optionLabel = option.label?.toLowerCase() || '';

                                // Если это отель — показываем только если inputValue не пустой и входит в label
                                if (
                                    option.type === 'hotel' &&
                                    (inputValue === '' || !optionLabel.includes(inputValue))
                                ) {
                                    return (
                                        <li {...props} style={{display: 'none'}}>
                                            {option.label}
                                        </li>
                                    );
                                }

                                return (
                                    <li {...props}>
                                        {option.label}
                                    </li>
                                );
                            }}

                            renderInput={(params) => <TextField {...params} label="Місто"/>}
                        />
                        <input type="hidden" name="location_id" value={selectedLocation?.identifier || ''}/>
                        <input type="hidden" name="location_type" value={selectedLocation?.type || ''}/>
                        <input type='hidden' name='booking_from'
                               value={format(selectionRange.startDate, 'yyyy-MM-dd')}/>
                        <input type='hidden' name='booking_to' value={format(selectionRange.endDate, 'yyyy-MM-dd')}/>
                    </Grid>
                    {/*<Grid item xs={5} md={1.5}>*/}
                    {/*    <FormControl fullWidth>*/}
                    {/*        <InputLabel id="meal-select-label">Харчування</InputLabel>*/}
                    {/*        <Select*/}
                    {/*            labelId="meal-select-label"*/}
                    {/*            multiple*/}
                    {/*            name="meals"*/}
                    {/*            value={meals}*/}
                    {/*            onChange={(event) => setMeals(event.target.value)}*/}
                    {/*            renderValue={(selected) => selected.map(opt => mealLabels[opt]).join(', ')}*/}
                    {/*        >*/}
                    {/*            {Object.entries(mealLabels).map(([key, label]) => (*/}
                    {/*                <MenuItem key={key} value={key}>*/}
                    {/*                    {label}*/}
                    {/*                </MenuItem>*/}
                    {/*            ))}*/}
                    {/*        </Select>*/}
                    {/*    </FormControl>*/}
                    {/*    /!* Передаємо вибрані значення у form *!/*/}
                    {/*    {meals.map((meal) => (*/}
                    {/*        <input type="hidden" name="meals" value={meal} key={meal}/>*/}
                    {/*    ))}*/}
                    {/*</Grid>*/}
                    {/*                        <FormControlLabel*/}
                    {/*        control={*/}
                    {/*            <Checkbox*/}
                    {/*                name="pets_allowed"*/}
                    {/*                checked={petsAllowed}*/}
                    {/*                onChange={(event) => setPetsAllowed(event.target.checked)}*/}
                    {/*            />*/}
                    {/*        }*/}
                    {/*        label="Можна з тваринами"*/}
                    {/*    />*/}

                    {/*    <input type="hidden" name="pets_allowed" value={petsAllowed}/>*/}
                    <Grid item xs={12} md={3}>
                        <FormControl fullWidth>
                            <TextField
                                label='Період'
                                value={`${daysShort[format(selectionRange.startDate, 'eee', {locale: uk})]}, ${format(selectionRange.startDate, 'dd MMM', {locale: uk})} - ${daysShort[format(selectionRange.endDate, 'eee', {locale: uk})]}, ${format(selectionRange.endDate, 'dd MMM', {locale: uk})}`}
                                onClick={handleModalRangePicker}
                            />
                        </FormControl>
                        <Modal
                            open={showRangeModal}
                            className='flex abs-center'
                        >
                            <Container maxWidth='md' className='flex' sx={{height: 'fit-content'}}>
                                <Grid item container className='flex abs-center' component={Paper}
                                      sx={{p: '20px', height: '100%', overflowY: 'auto'}}>
                                    <div
                                        style={{
                                            maxWidth: '100%',
                                            overflowX: 'hidden',
                                            width: isMobile ? '100%' : 'fit-content', // Адаптация для мобильных устройств
                                            height: isMobile ? '100%' : 'auto'
                                        }}
                                    >
                                        <DateRangePicker
                                            ranges={[selectionRange]}
                                            onChange={handleSelectDateRange}
                                            locale={customLocale}
                                            showSelectionPreview={false}
                                            months={2}
                                            showDateDisplay={false}
                                            direction={isMobile ? 'vertical' : 'horizontal'} // Вертикально на мобильных
                                            minDate={new Date()} // Запрещаем выбор дат до текущей
                                        />
                                    </div>
                                    <Button fullWidth
                                            variant='contained'
                                            sx={{color: 'white'}}
                                            endIcon={<DoneIcon/>}
                                            color='buttonSuccess'
                                            onClick={handleModalRangePicker}>
                                        <Typography>Готово</Typography>
                                    </Button>
                                </Grid>
                            </Container>
                        </Modal>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <DialogPeople searchParams={searchParams}/>
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <Button
                            variant="contained"
                            fullWidth
                            sx={{height: '100%'}}
                            type="submit"
                        >
                            Пошук
                        </Button>
                    </Grid>
                </Grid>


            </Form>
        </>
    );
}
export const ageArray = [
    {value: 0, label: '0 років'},
    {value: 1, label: '1 рік'},
    {value: 2, label: '2 роки'},
    {value: 3, label: '3 роки'},
    {value: 4, label: '4 роки'},
    {value: 5, label: '5 років'},
    {value: 6, label: '6 років'},
    {value: 7, label: '7 років'},
    {value: 8, label: '8 років'},
    {value: 9, label: '9 років'},
    {value: 10, label: '10 років'},
    {value: 11, label: '11 років'},
    {value: 12, label: '12 років'},
    {value: 13, label: '13 років'},
    {value: 14, label: '14 років'},
    {value: 15, label: '15 років'},
    {value: 16, label: '16 років'},
    {value: 17, label: '17 років'}
]


export const DialogPeople = ({searchParams}) => {
    // Универсальные геттеры: работают и для URLSearchParams (.get/.getAll),
    // и для объекта вида { key: ['value'] }
    const getOne = (sp, key) => {
        if (!sp) return null;
        if (typeof sp.get === 'function') return sp.get(key);
        if (Array.isArray(sp[key]) && sp[key].length > 0) return sp[key][0];
        return sp[key] ?? null;
    };
    const getAll = (sp, key) => {
        if (!sp) return [];
        if (typeof sp.getAll === 'function') return sp.getAll(key);
        if (Array.isArray(sp[key])) return sp[key];
        return sp[key] != null ? [sp[key]] : [];
    };

    const [open, setOpen] = useState(false);

// Стартовые значения: 1/0/1 по умолчанию
    const initialAdults = Number(getOne(searchParams, 'adults')) || 1;
    const initialChildren = Number(getOne(searchParams, 'children')) || 0;
    const initialRooms = Number(getOne(searchParams, 'rooms')) || 1;
    const initialAgesRaw = getAll(searchParams, 'children_ages').map(x => (x == null ? '' : String(x)));

    const [numAdults, setNumAdults] = useState(initialAdults);
    const [numChildren, setNumChildren] = useState(initialChildren);
    const [numRooms, setRooms] = useState(initialRooms);
    const [childrenAges, setChildrenAges] = useState(() => {
        const need = Math.max(initialChildren, 0);
        return initialAgesRaw.length < need
            ? [...initialAgesRaw, ...Array(need - initialAgesRaw.length).fill('')]
            : initialAgesRaw.slice(0, need);
    });

// 👇 НОВОЕ – если урл поменялся (например, снизу), подтягиваем новые значения
    useEffect(() => {
        const adults = Number(getOne(searchParams, 'adults')) || 1;
        const children = Number(getOne(searchParams, 'children')) || 0;
        const rooms = Number(getOne(searchParams, 'rooms')) || 1;
        const agesRaw = getAll(searchParams, 'children_ages').map(x => (x == null ? '' : String(x)));

        setNumAdults(adults);
        setNumChildren(children);
        setRooms(rooms);

        const need = Math.max(children, 0);
        const normalizedAges =
            agesRaw.length < need
                ? [...agesRaw, ...Array(need - agesRaw.length).fill('')]
                : agesRaw.slice(0, need);

        setChildrenAges(normalizedAges);
    }, [searchParams]);


    const isMobile = useMediaQuery('(max-width:600px)');

    const handleAgeChange = (index, value) => {
        setChildrenAges(prev => {
            const newAges = [...prev];
            newAges[index] = value;
            return newAges;
        });
    };

    const handleAdultsChange = (direction) => {
        setNumAdults(prev => prev + direction);
    };

    const handleChildrenChange = (direction) => {
        setNumChildren((prev) => {
            const newNumChildren = prev + direction;

            setChildrenAges((prevAges) => {
                if (newNumChildren < prevAges.length) {
                    return prevAges.slice(0, newNumChildren);
                } else if (newNumChildren > prevAges.length) {
                    return [...prevAges, ...Array(newNumChildren - prevAges.length).fill("")];
                }
                return prevAges;
            });

            return newNumChildren;
        });
    };

    const handleRoomsChange = (direction) => {
        setRooms(prev => prev + direction);
    };

    return (
        <>
            <FormControl fullWidth>
                <TextField
                    value={`${numAdults} дорослих, ${numChildren} дітей, ${numRooms} номерів`}
                    InputProps={{readOnly: true}}
                    onClick={() => setOpen(true)}
                />
            </FormControl>
            <input type='hidden' name='adults' value={numAdults}/>
            <input type='hidden' name='children' value={numChildren}/>
            <input type='hidden' name='rooms' value={numRooms}/>
            {childrenAges.map((age, index) => (
                <input name='children_ages' type='hidden' readOnly value={age} key={index}/>
            ))}

            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                fullScreen={isMobile} // Делаем полноэкранный вид на мобильных
            >
                <Container maxWidth="xs" sx={{py: 2, width: '100%', minHeight: isMobile ? '100vh' : 'auto'}}>
                    <Stack spacing={2} sx={{width: '100%'}} direction="column">
                        <Grid container spacing={1}>
                            <ChangeNumberComponent
                                handleChangeValue={handleAdultsChange}
                                disableConditionMinus={numAdults <= 1}
                                value={numAdults}
                                label="Дорослі"
                            />
                            <ChangeNumberComponent
                                handleChangeValue={handleChildrenChange}
                                disableConditionMinus={numChildren <= 0}
                                value={numChildren}
                                label="Діти"
                            />
                            <ChangeNumberComponent
                                handleChangeValue={handleRoomsChange}
                                disableConditionMinus={numRooms <= 1}
                                value={numRooms}
                                label="Номери"
                            />
                        </Grid>

                        {numChildren > 0 && (
                            <>
                                <Grid container>
                                    <Typography>Вкажіть вік дітей</Typography>
                                </Grid>
                                <Grid container>
                                    {Array.from({length: numChildren}, (_, index) => (
                                        <Grid item xs={6} sx={{p: 0.5}} key={index}>
                                            <FormControl fullWidth>
                                                <InputLabel id={`ch_${index}-label`}>Вік (обов'язково)</InputLabel>
                                                <Select
                                                    defaultValue={childrenAges[index] || ""}
                                                    onChange={(e) => handleAgeChange(index, e.target.value)}
                                                    required
                                                    label="Вік (обов'язково)"
                                                    labelId={`ch_${index}-label`}
                                                >
                                                    <MenuItem value="" disabled>Оберіть вік</MenuItem>
                                                    {ageArray.map((el) => (
                                                        <MenuItem value={el.value} key={el.value}>
                                                            {el.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                    ))}
                                </Grid>
                            </>
                        )}

                        <Grid container sx={{
                            position: isMobile ? 'sticky' : 'static',
                            bottom: 0,
                            background: 'white',
                            zIndex: 1000,
                            p: 1
                        }}>
                            <Button variant="outlined" fullWidth onClick={() => setOpen(false)}>Готово</Button>
                        </Grid>
                    </Stack>
                </Container>
            </Dialog>
        </>
    );
};

export const ChangeNumberComponent = ({handleChangeValue, disableConditionMinus, value, label}) => {
    return (
        <>
            <Grid item container xs={6} sx={{alignItems: 'center'}}>
                <Typography>{label}</Typography>
            </Grid>
            <Grid item xs={6}>
                <Stack direction='row' sx={{width: 'fit-content', borderRadius: 1, border: '1px solid black'}}>
                    <IconButton
                        sx={{
                            height: '40px',
                            width: '40px',
                        }}
                        onClick={() => handleChangeValue(-1)}
                        disabled={disableConditionMinus}
                        variant="outlined"
                    >
                        <RemoveIcon/>
                    </IconButton>
                    <Box
                        sx={{
                            padding: '6px 12px',
                            minWidth: '40px',
                            textAlign: 'center',
                            flexGrow: 1,
                        }}
                    >
                        {value}
                    </Box>
                    <IconButton
                        sx={{
                            height: '40px',
                            width: '40px',
                        }}
                        onClick={() => handleChangeValue(1)}
                        variant="outlined"
                    >
                        <AddIcon/>
                    </IconButton>
                </Stack>
            </Grid>
        </>
    )
}
